/**
 * Trial chamber (vanilla trial_chambers), reduced to one sealed underground
 * hall of tuff and copper: a central arena with a trial spawner on a copper
 * pedestal, two side rooms with their own trial spawners, a vault room with a
 * vault block (opened with a trial key, game/trials.ts), and four store rooms
 * with corridor loot chests. Nothing targetable ever stands above a chest.
 *
 * Block layout (local frame, 33 x 33, floor at y): store rooms in the four
 * corners (u/v 2..8 and 24..30), spawner rooms south-west / south-east, the
 * vault south-middle, the arena in the centre, chests north-middle.
 */

import { AIR, IRON_BARS_ID, TRIAL_SPAWNER_ID, VAULT_ID } from '../../blocks';
import { SEA_LEVEL } from '../../constants';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, cell, centredPiece, F, int, type Piece, type Rng, seedOf, type StructureType } from './kit';

const TUFF = block('Tuff');
const WALL = cell.mix([[TUFF, 6], [block('Cut Copper'), 1], [block('Exposed Copper'), 1]]);
const FLOOR = cell.mix([[TUFF, 5], [block('Cut Copper'), 2], [block('Oxidized Copper'), 1]]);
const COPPER = block('Block of Copper');
const GLOW = block('Glowstone');
export const TRIAL_SIZE = 33;
export const TRIAL_HEIGHT = 8;

function storeRoom(b: Builder, u0: number, v0: number, doorU: number, doorV: number, faceToMiddle: number, chest: boolean): void {
  const y = b.y;
  b.room(u0, y - 1, v0, u0 + 6, y + 5, v0 + 6, WALL);
  b.fill(u0 + 1, y, v0 + 1, u0 + 5, y, v0 + 5, FLOOR);
  b.fill(u0 + 1, y + 1, v0 + 1, u0 + 5, y + 4, v0 + 5, AIR);
  b.set(doorU, y, doorV, FLOOR);
  b.fill(doorU, y + 1, doorV, doorU, y + 2, doorV, AIR);
  b.set(u0 + 3, y + 3, v0 + 3, GLOW);
  if (chest) b.chest(u0 + 3, y + 1, v0 + 3, faceToMiddle);
}

function buildChamber(b: Builder): void {
  const y = b.y, N = TRIAL_SIZE - 1;
  // hall shell, floor, hollow
  b.room(0, y - 1, 0, N, y + TRIAL_HEIGHT, N, WALL);
  b.fill(1, y, 1, N - 1, y, N - 1, FLOOR);
  b.fill(1, y + 1, 1, N - 1, y + TRIAL_HEIGHT - 1, N - 1, AIR);
  // the arena: a copper pedestal with the trial spawner, a ring of tuff pillars and bars
  b.fill(12, y, 12, 20, y, 20, block('Cut Copper'));
  b.fill(15, y, 15, 17, y, 17, COPPER);
  b.set(16, y + 1, 16, TRIAL_SPAWNER_ID);
  for (const [u, v] of [[12, 12], [20, 12], [12, 20], [20, 20]]) {
    b.fill(u, y + 1, v, u, y + 4, v, TUFF);
    b.fill(u, y + 5, v, u, y + TRIAL_HEIGHT - 1, v, WALL);
  }
  for (let k = 13; k <= 19; k += 2) {
    for (const [u, v] of [[k, 11], [k, 21], [11, k], [21, k]]) b.fill(u, y + 1, v, u, y + 2, v, IRON_BARS_ID);
  }
  // ceiling lights over the arena
  for (const [u, v] of [[8, 16], [24, 16], [16, 8], [16, 24], [16, 16]]) b.set(u, y + TRIAL_HEIGHT - 1, v, GLOW);
  // four corner store rooms (chest each), doors toward the hall
  storeRoom(b, 2, 2, 5, 8, F.PV, true);
  storeRoom(b, 24, 2, 27, 8, F.PV, true);
  // south corners: spawner rooms
  for (const [u0, v0, doorU] of [[2, 24, 5], [24, 24, 27]] as const) {
    storeRoom(b, u0, v0, doorU, v0, F.PV, false);
    b.set(u0 + 3, y + 1, v0 + 3, TRIAL_SPAWNER_ID);
  }
  // vault room, south-middle: a vault block on a copper plinth
  b.room(12, y - 1, 25, 20, y + 5, 31, WALL);
  b.fill(13, y, 26, 19, y, 30, FLOOR);
  b.fill(13, y + 1, 26, 19, y + 4, 30, AIR);
  b.fill(16, y + 1, 25, 16, y + 2, 25, AIR);
  b.set(16, y, 25, FLOOR);
  b.fill(15, y, 28, 17, y, 29, COPPER);
  b.set(16, y + 1, 28, VAULT_ID);
  for (const u of [13, 19]) b.fill(u, y + 1, 27, u, y + 3, 27, IRON_BARS_ID);
  b.set(16, y + 4, 28, GLOW);
  // north-middle supply niche with a chest facing the hall
  b.fill(14, y + 1, 1, 18, y + 3, 2, AIR);
  b.chest(16, y + 1, 1, F.PV);
}

function trialChamber(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  const y = 14 + int(rnd, 6);
  if (c.height - 12 < y + TRIAL_HEIGHT) return null;
  if (c.height < SEA_LEVEL - 30) return null;
  return [Object.assign(centredPiece(x, z, 0, TRIAL_SIZE, TRIAL_SIZE, y, false, seedOf(rnd), buildChamber), { loot: 'trial_chambers' as const })];
}

export const TRIAL_CHAMBERS: StructureType = {
  name: 'Trial Chambers', spacing: 34, separation: 12, salt: 0x7a1c4, reach: 2, hud: false, generate: trialChamber,
};
